import PersonAdd from '@mui/icons-material/PersonAdd'
import { Alert, Box, Button, Link, Stack } from '@mui/material'
import { useState } from 'preact/hooks'
import { api } from '../api/client'
import type { Assignment } from '../api/generated/Assignment'
import type { AssignmentUpdate } from '../api/generated/AssignmentUpdate'
import type { Decision } from '../api/generated/Decision'
import type { NewAssignment } from '../api/generated/NewAssignment'
import { useAction } from '../api/useAction'
import { VolunteerCard } from '../volunteers/VolunteerCard'
import { EnrollDialog } from './EnrollDialog'
import { AttendanceChip, OverlapWarning, StatusChip, volunteerName } from './status'

/** An organizer's view of a shift's volunteers, to assign, remove or enroll them. */
export function ShiftAssignments({
  slug,
  shiftId,
  assignments,
  onChange,
}: {
  slug: string
  shiftId: string
  assignments: Assignment[]
  /** Receives all the event's assignments after a change. */
  onChange: (assignments: Assignment[]) => void
}) {
  const path = `/events/${slug}/assignments`
  const [enrolling, setEnrolling] = useState(false)
  const [showingCard, setShowingCard] = useState<Assignment>()
  const { error, pending, run } = useAction()

  const decide = (assignment: Assignment, status: Decision) =>
    run(async () => {
      const body: AssignmentUpdate = { status }
      onChange(await api.patch<Assignment[]>(`${path}/${assignment.id}`, body))
    })
  const enroll = async (email: string) => {
    const body: NewAssignment = { shift_id: shiftId, email }
    onChange(await api.post<Assignment[]>(path, body))
    setEnrolling(false)
  }

  return (
    <Box sx={{ width: '100%', pl: 2 }}>
      {assignments.map((assignment) => (
        <Stack
          key={assignment.id}
          direction="row"
          sx={{ alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}
        >
          <Link
            component="button"
            type="button"
            variant="body2"
            title="Voir sa fiche"
            sx={{ flex: 1, minWidth: 120, textAlign: 'left' }}
            onClick={() => setShowingCard(assignment)}
          >
            {volunteerName(assignment)}
          </Link>
          <StatusChip status={assignment.status} />
          <AttendanceChip assignment={assignment} />
          {assignment.status !== 'accepted' && (
            <Button size="small" disabled={pending} onClick={() => decide(assignment, 'accepted')}>
              Affecter
            </Button>
          )}
          {(assignment.status === 'accepted' || assignment.status === 'offered') && (
            <Button
              size="small"
              color="error"
              disabled={pending}
              onClick={() => decide(assignment, 'rejected')}
            >
              Retirer
            </Button>
          )}
          <OverlapWarning assignment={assignment} />
        </Stack>
      ))}
      {error && <Alert severity="error">{error}</Alert>}
      <Button size="small" startIcon={<PersonAdd />} onClick={() => setEnrolling(true)}>
        Inscrire un bénévole
      </Button>
      {enrolling && <EnrollDialog onSubmit={enroll} onClose={() => setEnrolling(false)} />}
      {showingCard && (
        <VolunteerCard
          slug={slug}
          volunteer={showingCard}
          onChange={onChange}
          onClose={() => setShowingCard(undefined)}
        />
      )}
    </Box>
  )
}
